import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaSearch, FaTimes, FaCube } from 'react-icons/fa';
import {
  CATEGORY_PILLAR,
  PILLARS,
  SKILL_CATEGORIES,
  pillarOf,
  skillsWithProjects,
} from '../helpers/SkillsData';
import { useLanguage } from '../context/LanguageContext';
import { translations } from '../translations/translations';
import '../styles/Skills.css';

function Skills() {
  const { language } = useLanguage();
  const t = translations[language].skillsPage;
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  // Pillar lives in ?pillar= so detail-page back and shared links keep the
  // filter. Legacy ?cat= links resolve to the category's parent pillar.
  const rawPillar = searchParams.get('pillar') || '';
  const legacyPillar = CATEGORY_PILLAR[searchParams.get('cat') || ''] || '';
  const pillar = PILLARS.includes(rawPillar) ? rawPillar : legacyPillar || 'all';

  const allSkills = useMemo(() => skillsWithProjects(), []);
  const totalProjects = useMemo(
    () => new Set(allSkills.flatMap((s) => s.projects.map((p) => p.projectId))).size,
    [allSkills]
  );

  // Keep ?q= / ?pillar= shareable, and in sync with chip deep-links from project pages
  const applyParams = (q, p) => {
    const params = {};
    if (q.trim()) params.q = q;
    if (p !== 'all') params.pillar = p;
    setSearchParams(params, { replace: true });
  };

  const updateQuery = (value) => {
    setQuery(value);
    applyParams(value, pillar);
  };

  const setPillar = (next) => applyParams(query, next);

  // Back/forward navigation and in-app links must update the search box too
  const urlQuery = searchParams.get('q') || '';
  useEffect(() => {
    setQuery(urlQuery);
  }, [urlQuery]);

  useEffect(() => {
    const prev = document.title;
    document.title = `${t.title} · Teoh Jia Kang`;
    return () => {
      document.title = prev;
    };
  }, [t.title]);

  const q = query.trim().toLowerCase();
  const matches = (skill) => {
    if (pillar !== 'all' && pillarOf(skill.category) !== pillar) return false;
    if (!q) return true;
    const haystack = [
      skill.name,
      ...(skill.aliases || []),
      t.categories[skill.category] || '',
      t.pillars[pillarOf(skill.category)]?.name || '',
      ...skill.projects.map((p) => p.name),
    ]
      .join(' ')
      .toLowerCase();
    return haystack.includes(q);
  };

  const filtered = allSkills.filter(matches);
  // Pillar → category sub-groups, both in registry order, empties dropped
  const grouped = PILLARS.map((pk) => ({
    key: pk,
    categories: SKILL_CATEGORIES.filter((c) => CATEGORY_PILLAR[c] === pk)
      .map((c) => ({ key: c, skills: filtered.filter((s) => s.category === c) }))
      .filter((g) => g.skills.length > 0),
  })).filter((g) => g.categories.length > 0);

  return (
    <div className='skills-page'>
      <motion.div
        className='skills-container'
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <h1 className='page-title'>{t.title}</h1>
        <p className='skills-subtitle'>{t.subtitle}</p>
        <p className='skills-summary'>
          {allSkills.length} {t.skillsWord} · {totalProjects} {t.projectPlural}
        </p>

        <div className='skills-toolbar'>
          <div className='skills-search'>
            <FaSearch aria-hidden='true' />
            <input
              type='search'
              value={query}
              placeholder={t.searchPlaceholder}
              aria-label={t.searchPlaceholder}
              onChange={(e) => updateQuery(e.target.value)}
            />
            {query && (
              <button
                type='button'
                className='skills-search-clear'
                aria-label={t.clear}
                onClick={() => updateQuery('')}
              >
                <FaTimes />
              </button>
            )}
          </div>
          <div className='skills-category-pills'>
            <button
              type='button'
              className={`skills-pill ${pillar === 'all' ? 'active' : ''}`}
              aria-pressed={pillar === 'all'}
              onClick={() => setPillar('all')}
            >
              {t.all}
            </button>
            {PILLARS.map((key) => (
              <button
                key={key}
                type='button'
                className={`skills-pill ${pillar === key ? 'active' : ''}`}
                aria-pressed={pillar === key}
                onClick={() => setPillar(pillar === key ? 'all' : key)}
              >
                {t.pillars[key].name}
              </button>
            ))}
          </div>
          {(q || pillar !== 'all') && (
            <p className='skills-result-count' aria-live='polite'>
              {filtered.length} {filtered.length === 1 ? t.skillWordSingular : t.skillsWord}
            </p>
          )}
        </div>

        {grouped.length === 0 && (
          <div className='skills-empty'>
            <p>{t.empty}</p>
            <button type='button' className='skills-clear-btn' onClick={() => { setQuery(''); applyParams('', 'all'); }}>
              {t.clear}
            </button>
          </div>
        )}

        {grouped.map((group) => (
          <section key={group.key} className='skills-pillar-section'>
            <h2 className='pillar-heading'>{t.pillars[group.key].name}</h2>
            <p className='pillar-tagline'>{t.pillars[group.key].tagline}</p>
            {group.categories.map((cat) => (
              <div key={cat.key} className='skills-category-section'>
                {/* Sub-label only where a pillar spans several categories.
                    Not a heading — skill names are the h3s under the pillar h2 */}
                {group.categories.length > 1 && (
                  <p className='section-label'>{t.categories[cat.key]}</p>
                )}
                <div className='skills-grid'>
                  {cat.skills.map((skill, index) => (
                    <motion.div
                      key={skill.name}
                      className='skill-entry glass'
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.35, delay: Math.min(index * 0.04, 0.3) }}
                    >
                      <Link
                        className='skill-entry-link'
                        to={`/skills/${skill.slug}`}
                        state={{ fromSkills: location.search }}
                        title={t.viewDetails}
                      >
                        <div className='skill-entry-head'>
                          <h3>
                            {skill.name}
                            {skill.showcases.length > 0 && (
                              <span className='skill-3d-badge'>
                                <FaCube aria-hidden='true' /> 3D
                              </span>
                            )}
                          </h3>
                          <span className='skill-year-span'>{skill.yearSpan}</span>
                        </div>
                        <div className='skill-thumbs'>
                          {skill.projects.slice(0, 3).map((p) => (
                            <img
                              key={p.projectId}
                              src={p.thumb}
                              alt=''
                              width={46}
                              height={34}
                              loading='lazy'
                              decoding='async'
                            />
                          ))}
                          <span className='skill-count'>
                            {skill.projects.length}{' '}
                            {skill.projects.length === 1 ? t.projectSingular : t.projectPlural}
                          </span>
                        </div>
                      </Link>
                      <ul className='skill-project-list'>
                        {skill.projects.map((p) => (
                          <li key={p.projectId}>
                            <Link to={`/project/${p.projectId}`}>
                              <span className='skill-project-name'>{p.name}</span>
                              <span className='skill-project-year'>{p.year}</span>
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </motion.div>
                  ))}
                </div>
              </div>
            ))}
          </section>
        ))}
      </motion.div>
    </div>
  );
}

export default Skills;
